import {useEffect, useRef} from "react";
import {useToast, ToastType} from "../FCToast/FCToast";

export enum TypeAlertMsg {
    ERROR = 0,
    INFO = 1,
    WARNING = 2,
    SUCCESS = 3
}

type AlertMsgProps = {
    textMsg: string;
    title?: string;
    type: TypeAlertMsg;
    activate: boolean;
};

const TYPE_MAP: Record<TypeAlertMsg, ToastType> = {
    [TypeAlertMsg.ERROR]: "error",
    [TypeAlertMsg.INFO]: "info",
    [TypeAlertMsg.WARNING]: "warning",
    [TypeAlertMsg.SUCCESS]: "success",
};

/**
 * Pont de rétro-compatibilité : conserve l'API historique de FCAlertMsg
 * mais déclenche un toast `FCToast` à l'apparition.
 *
 * Le toast est émis :
 *  - une fois au montage si `activate === true`
 *  - à chaque transition `activate: false → true`
 *  - à chaque changement de `textMsg` quand `activate === true` (nouveau message)
 *
 * Ne rend rien dans le DOM — l'affichage est géré par FCToastProvider.
 */
const FCAlertMsg: React.FunctionComponent<AlertMsgProps> = (props) => {
    const toast = useToast();

    // Mémoïse les valeurs précédentes pour ne pas spammer le toast
    const prevActivateRef = useRef<boolean>(false);
    const prevMsgRef = useRef<string>("");

    useEffect(() => {
        const justActivated = props.activate && !prevActivateRef.current;
        const messageChangedWhileActive = props.activate && props.textMsg !== prevMsgRef.current;

        if ((justActivated || messageChangedWhileActive) && props.textMsg) {
            toast.show(TYPE_MAP[props.type], props.textMsg, props.title);
        }

        prevActivateRef.current = props.activate;
        prevMsgRef.current = props.textMsg;
    }, [props.activate, props.textMsg, props.type, props.title, toast]);

    return null;
}

export default FCAlertMsg;
